/**
 * Internal mobility: starts in a different position over the twelve months to the panel's
 * date, employees first.
 *
 * COUNTED AS THE REMCO DATA PACK COUNTS MOVES. A start counts when the person held some other
 * position before, however long ago. Two kinds of start are therefore inside the totals
 * without being movement inside the workforce, and are shown apart:
 *  - a return: no position at all in the twelve months before the start;
 *  - a restart: in those twelve months, only the position being started again.
 * No tile says "moved inside the organisation" for that reason. People are never added across
 * kinds: one person can return and then move.
 *
 * EMPLOYEES LEAD. The employee rate is employees who started a different position over
 * employees in post; every engaged worker's rate is over every filled position, sessional
 * included. The two sit side by side and are never added.
 *
 * NULL IS WITHHELD, ABSENT IS NOT AVAILABLE. A withheld count shows as a dash with the rule
 * attached and never sizes a bar. The grade and function splits are each shown only when the
 * role catalogue could place nearly every position involved; otherwise the panel says how many
 * it could not place.
 *
 * A copy stored before the daily recalculation counts moves on an earlier definition. It is not
 * shown beside the pack's measure: the panel reads not available for that date.
 *
 * Window: twelve months to the panel's date. Follows neither the period picker nor the basis
 * toggle.
 */
import React, { useMemo, type ReactNode } from 'react';
import { BarRow } from './BarRow';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  ACCENT,
  CATEGORY_COLORS,
  CATEGORY_ORDER,
  Def,
  INK,
  categoryLabel,
  fmtDay,
  fmtNum,
  fmtPct,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type MobilityExtra,
  type MobilityScope,
  type WorkforceBasis,
  type WorkforcePanels,
  type WorkforceWindow,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  /** Part of the shared panel signature. Mobility is deliberately basis-independent: it leads
   *  with employees and states every engaged worker beside them on every basis. */
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

const SUPPRESSION_NOTE =
  'Withheld: fewer than 5 people, or a figure that would let a smaller one elsewhere on this page '
  + 'be worked out. It is not zero, and it is still in the totals.';

const WITHHELD_SUB = 'withheld to protect small groups';
const NOT_AVAILABLE_SUB = 'not available for this date';

/** What counts as a start. Said once, used in the tooltip and the footnote. */
const COUNTING_RULE =
  'A start counts here when the person held some other position before, however long ago, as the '
  + 'Remco data pack counts moves. A person who has only ever held one position, starting it '
  + 'again, is not counted.';

const KINDS_APART =
  'Returns (no position for a year or more) and restarts (starting again the position held in the '
  + 'twelve months before) are inside the totals and shown apart: neither is movement inside the '
  + 'workforce.';

const RATES =
  'The employee rate is employees who started a different position over employees in post. The '
  + 'all-engaged rate is everyone who did over every filled position, sessional included. Both '
  + 'rates include returns and restarts.';

const KINDS_COPY =
  'The Remco data pack counts a start as a move whenever the person held some other position '
  + 'before, however long ago. A return is a person back after a year or more with no position. A '
  + 'restart is a person starting again the position they held last time, counted from an older, '
  + 'different position. People are not added across the three lines: one person can return and '
  + 'then move.';

interface CutRow {
  key: string;
  label: string;
  color: string;
  /** Tooltip beside the label, for a label that needs one. */
  note?: string;
}

const DESTINATION_ROWS: CutRow[] = [
  ...CATEGORY_ORDER.map((k) => ({ key: k, label: categoryLabel(k), color: CATEGORY_COLORS[k] ?? '#B9C4D6' })),
  {
    key: 'not_recorded',
    label: 'Position since closed',
    color: '#8A94A6',
    note: 'The position started is no longer in the establishment at the date shown, so its contract cannot be read.',
  },
];

const GRADE_ROWS: CutRow[] = [
  { key: 'higher', label: 'To a higher grade', color: INK },
  { key: 'same', label: 'Same grade', color: ACCENT },
  { key: 'lower', label: 'To a lower grade', color: '#A8443A' },
  {
    key: 'not_recorded',
    label: 'Grade not stated',
    color: '#B9C4D6',
    note: 'A non-graded position on either side of the start, so the direction cannot be stated. It is not a same-grade move.',
  },
];

const FUNCTION_ROWS: CutRow[] = [
  { key: 'within', label: 'Within the same function', color: INK },
  { key: 'across', label: 'Across functions', color: '#0B7F72' },
  { key: 'not_recorded', label: 'Function not recorded', color: '#B9C4D6' },
];

const defBody = (title: string, body: string): ReactNode => (
  <div className="space-y-1.5">
    <p className="font-semibold text-white">{title}</p>
    <p>{body}</p>
  </div>
);

/** Panel-local definitions. Anything /metric-definitions publishes for the same key wins; these
 *  only fill the gap. */
const OWN_DEFS: Record<string, ReactNode> = {
  mobility: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Internal mobility</p>
      <p>{COUNTING_RULE}</p>
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        <li>{KINDS_APART}</li>
        <li>Twelve months to the date shown. It does not follow the period picker.</li>
      </ul>
    </div>
  ),
  mobility_people_employees: defBody(
    'Employees who started a different position',
    'Employees in post at the date shown who started a different position over the window, as a percentage of employees in post. Returns and restarts are included.',
  ),
  mobility_moves: defBody(
    'Employee starts in a different position',
    'Every start counted for those employees, returns and restarts included. A person who started twice counts twice here and once among the people.',
  ),
  mobility_people_all: defBody(
    'All engaged workers who started a different position',
    'Everyone behind the starts, sessional included, as a percentage of every filled position. Returns and restarts are included.',
  ),
  mobility_kinds: defBody('Returns and restarts', KINDS_COPY),
  mobility_destination: defBody(
    'Where the starts went',
    "Every engaged worker's starts, by the contract of the position started, as that position stands at the date shown.",
  ),
  mobility_grade: defBody(
    'Move type',
    'Each start classified by the grade either side of it. For a return or a restart the comparison is with a position left more than a year earlier.',
  ),
  mobility_function: defBody(
    'Within or across functions',
    'The same starts classified by function either side: a second cut of the same total, not an addition to the move types.',
  ),
};

/** A withheld figure: a dash with the rule attached, never 0. */
const Withheld: React.FC<{ def: DefLookup }> = ({ def }) => (
  <span className="inline-flex items-center justify-end text-gray-400">
    —<span className="sr-only">withheld</span>
    <Def def={def} k="suppression" text={SUPPRESSION_NOTE} />
  </span>
);

const NotAvailable: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="rounded border border-gray-200 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-600">
    {children}
  </div>
);

/**
 * The words that date this panel, from the block's own provenance entry, never the frame's.
 * A block stamped with a kind reads through panelDateLabel. A copy stored before blocks were
 * stamped carries no kind, and panelDateLabel would call its window a point in time, so it is
 * dated by the window it carries, or the day it runs to. null: nothing to date it by.
 */
function dateWords(
  panels: WorkforcePanels | null,
  block: string,
  own?: WorkforceWindow | null,
): string | null {
  const entry = panels?.panels_provenance?.[block];
  if (!entry?.computed_for) return null;
  if (entry.kind) return panelDateLabel(panels, block).replace(/^To /, 'to ');
  if (own?.from && own.to) return `${fmtDay(own.from)} – ${fmtDay(own.to)}`;
  return `to ${fmtDay(entry.computed_for)}`;
}

/** null: withheld. undefined: the response does not carry it for this date. */
function figure(n: number | null | undefined, sub: string, def: DefLookup): { value: ReactNode; sub: string } {
  if (n === undefined) return { value: '—', sub: NOT_AVAILABLE_SUB };
  if (n === null) return { value: <Withheld def={def} />, sub: WITHHELD_SUB };
  return { value: fmtNum(n), sub };
}

function scopeSub(scope: MobilityScope, base: number | undefined, baseWords: string): string {
  const rate = scope.rate === null ? 'rate withheld' : fmtPct(scope.rate);
  return base === undefined ? rate : `of ${fmtNum(base)} ${baseWords} · ${rate}`;
}

/** Starts from another position held in the twelve months before: the scope's starts less its
 *  returns and restarts, only when all three are published. Any one withheld leaves it withheld. */
function fromAnotherPosition(scope: MobilityScope): number | null {
  const { moves, returners, restarts } = scope;
  return typeof moves === 'number' && typeof returners?.moves === 'number' && typeof restarts?.moves === 'number'
    ? moves - returners.moves - restarts.moves
    : null;
}

const notShown = (what: string, unplaced?: { positions: number; of: number }) =>
  unplaced
    ? `${what} movement is not shown for this date: the role catalogue could not place ${fmtNum(unplaced.positions)} of the ${fmtNum(unplaced.of)} positions involved, most of them retitled since.`
    : `${what} movement is not shown for this date.`;

function hasWithheld(mob: MobilityExtra): boolean {
  const { employees: e, all_engaged: a } = mob;
  return [
    e.people,
    e.moves,
    a.people,
    e.returners?.moves,
    e.returners?.people,
    e.restarts?.moves,
    e.restarts?.people,
    mob.into_employee_positions,
    ...Object.values(mob.destination ?? {}),
    ...(mob.grade_published === false ? [] : Object.values(mob.grade ?? {})),
    ...(mob.function_published === false ? [] : Object.values(mob.function ?? {})),
  ].some((v) => v === null);
}

/** One cut of the starts as bars, or the sentence saying why it is not shown. */
const CutSection: React.FC<{
  title: string;
  defKey: string;
  def: DefLookup;
  rows: CutRow[];
  values?: Record<string, number | null | undefined>;
  notShownText?: string;
}> = ({ title, defKey, def, rows, values, notShownText }) => {
  if (!values && !notShownText) return null;
  const shown: CutRow[] = values
    ? [
        ...rows.filter((r) => values[r.key] !== undefined),
        ...Object.keys(values)
          .filter((k) => !rows.some((r) => r.key === k))
          .map((k): CutRow => ({ key: k, label: k, color: '#B9C4D6' })),
      ]
    : [];
  const max = Math.max(
    0,
    ...shown.map((r) => values?.[r.key]).filter((v): v is number => typeof v === 'number'),
  );
  return (
    <div>
      <div className="mb-1 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        {title}
        <Def def={def} k={defKey} />
      </div>
      {notShownText ? (
        <p className="text-[12px] leading-snug text-gray-600">{notShownText}</p>
      ) : (
        shown.map((r) => {
          const value = values?.[r.key] ?? null;
          return (
            <BarRow
              key={r.key}
              label={
                r.note ? (
                  <span className="inline-flex max-w-full items-center">
                    <span className="truncate">{r.label}</span>
                    <Def def={def} k={`${defKey}_${r.key}`} text={r.note} />
                  </span>
                ) : (
                  r.label
                )
              }
              value={value}
              max={max}
              color={r.color}
              labelWidth={190}
              valueText={value === null ? <Withheld def={def} /> : undefined}
            />
          );
        })
      )}
    </div>
  );
};

const KindRow: React.FC<{ label: string; starts: number | null; people?: number | null; def: DefLookup }> = ({
  label,
  starts,
  people,
  def,
}) => (
  <div className="flex items-baseline justify-between gap-3 py-1">
    <dt className="min-w-0 text-gray-700">{label}</dt>
    <dd className="shrink-0 text-right tabular-nums text-gray-500">
      <span className="font-semibold text-heritage-blue">
        {starts === null ? <Withheld def={def} /> : fmtNum(starts)}
      </span>{' '}
      starts
      {people === undefined ? null : (
        <>
          {' · '}
          <span className="font-semibold text-heritage-blue">
            {people === null ? <Withheld def={def} /> : fmtNum(people)}
          </span>{' '}
          people
        </>
      )}
    </dd>
  </div>
);

const MobilityBody: React.FC<{ mob: MobilityExtra; def: DefLookup }> = ({ mob, def }) => {
  const emp = mob.employees;
  const all = mob.all_engaged;
  const people = figure(emp.people, scopeSub(emp, emp.in_post, 'employees in post'), def);
  const starts = figure(emp.moves, 'returns and restarts included', def);
  const everyone = figure(all.people, scopeSub(all, all.filled_positions, 'filled positions'), def);
  const { returners, restarts } = emp;
  const into = mob.into_employee_positions;

  return (
    <div className="space-y-4">
      <TileRow min="150px">
        <MetricTile
          label="Employees who started a different position"
          value={people.value}
          sub={people.sub}
          defKey="mobility_people_employees"
          def={def}
          size="sm"
        />
        <MetricTile
          label="Employee starts in a different position"
          value={starts.value}
          sub={starts.sub}
          defKey="mobility_moves"
          def={def}
          size="sm"
        />
        <MetricTile
          label="All engaged workers who started a different position"
          value={everyone.value}
          sub={everyone.sub}
          defKey="mobility_people_all"
          def={def}
          size="sm"
        />
      </TileRow>

      {returners && restarts ? (
        <div>
          <div className="mb-1 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Inside the employee totals
            <Def def={def} k="mobility_kinds" />
          </div>
          <dl className="divide-y divide-gray-100 text-[12px]">
            <KindRow
              label="Returned after a year or more with no position"
              starts={returners.moves}
              people={returners.people}
              def={def}
            />
            <KindRow
              label="Started again a position they held in the twelve months before"
              starts={restarts.moves}
              people={restarts.people}
              def={def}
            />
            <KindRow
              label="Moved from another position they held in the twelve months before"
              starts={fromAnotherPosition(emp)}
              def={def}
            />
          </dl>
          <p className="pt-1 text-[11px] leading-snug text-slate-400">
            Returns and restarts are inside these totals but are not movement inside the workforce.{' '}
            {KINDS_COPY}
          </p>
        </div>
      ) : null}

      {mob.destination ? (
        <div>
          <CutSection
            title="Where the starts went · every engaged worker"
            defKey="mobility_destination"
            def={def}
            rows={DESTINATION_ROWS}
            values={mob.destination}
          />
          {into === null ? (
            <p className="pt-1 text-[11px] text-slate-400">
              How many of these starts were into permanent or fixed-term positions is withheld to
              protect small groups.
            </p>
          ) : typeof into === 'number' ? (
            <p className="pt-1 text-[11px] text-slate-400">
              {fmtNum(into)} of these starts were into permanent or fixed-term positions.
            </p>
          ) : null}
        </div>
      ) : null}

      <CutSection
        title="Move type"
        defKey="mobility_grade"
        def={def}
        rows={GRADE_ROWS}
        values={mob.grade_published === false ? undefined : mob.grade}
        notShownText={mob.grade_published === false ? notShown('Grade', mob.grade_unplaced) : undefined}
      />
      <CutSection
        title="Within or across functions"
        defKey="mobility_function"
        def={def}
        rows={FUNCTION_ROWS}
        values={mob.function_published === false ? undefined : mob.function}
        notShownText={mob.function_published === false ? notShown('Function', mob.function_unplaced) : undefined}
      />
      {mob.role_catalogue_as_at
      && (mob.grade || mob.function || mob.grade_published === false || mob.function_published === false) ? (
        <p className="text-[11px] text-slate-400">
          Function and grade as the role catalogue stood on {fmtDay(mob.role_catalogue_as_at)}.
        </p>
      ) : null}
    </div>
  );
};

export const MobilityPanel: React.FC<Props> = ({ panels, loading, def }) => {
  const defs = useMemo<DefLookup>(() => (k: string) => def(k) ?? OWN_DEFS[k], [def]);

  const block = readExtras(panels, 'mobility');
  // A copy stored before the daily recalculation carries the earlier definition's keys.
  const storedOnly = !!block && 'promotions' in block;
  const mob: MobilityExtra | null =
    block && !('promotions' in block) && block.employees && block.all_engaged ? block : null;

  const dated = dateWords(panels, 'mobility', block?.window ?? null);
  const windowNote = `Twelve months${dated ? `, ${dated}` : ''}. Follows neither the period picker nor the basis toggle.`;

  const footnote = mob ? (
    <>
      {COUNTING_RULE} {RATES}
      {mob.grade && mob.function && mob.grade_published !== false && mob.function_published !== false
        ? ' Move type and the function split are two cuts of the same starts, so they do not add together.'
        : ''}
      {hasWithheld(mob) ? (
        <span className="text-amber-700">
          {' '}
          A dash is a figure withheld to protect small groups: it is not zero, and it is still in the
          totals.
        </span>
      ) : null}
    </>
  ) : undefined;

  return (
    <PanelShell title="Internal mobility" defKey="mobility" def={defs} window={windowNote} footnote={footnote}>
      {loading && !panels ? (
        <PanelSkeleton height={260} />
      ) : !panels ? (
        <PanelUnavailable what="Internal mobility" />
      ) : mob ? (
        <MobilityBody mob={mob} def={defs} />
      ) : (
        <NotAvailable>
          {storedOnly
            ? 'Internal mobility is not available for this date: the figures stored for it count moves on an earlier definition than the Remco data pack now uses.'
            : 'Internal mobility is not available for this date.'}
        </NotAvailable>
      )}
    </PanelShell>
  );
};
